import { observable, action, computed, autorun, makeObservable, runInAction, } from 'mobx';
import { isUuid, isReference } from '../util/validate';
import { assert } from '../util/assert';
import ClientStore from './ClientStore';
import { ConsentGroupStore } from './ConsentGroupStore';
import OcclStore from './OcclStore';
import Service from '../util/Service';
import SessionStorage from '../util/SessionStorage';
import { HistoryStore } from './HistoryStore';
import Tracker from '../classes/Tracker';
export class ComsStore {
    constructor() {
        this.consentGroups = undefined;
        this.details = undefined;
        this.identity = undefined;
        this.config = undefined;
        this.fromStorage = false;
        this.references = [];
        this.showReferences = false;
        this.showVersions = false;
        this.isExistingRegister = false;
        this.service = new Service();
        this.tracker = new Tracker();
        this.error = undefined;
        makeObservable(this, {
            consentGroups: observable,
            identity: observable,
            error: observable,
            references: observable,
            fatal: computed,
            init: action,
            isDataLoaded: computed,
            isOcclReady: computed,
            hasModifiedConsents: computed,
        });
    }
    reset() {
        runInAction(() => {
            this.config = undefined;
            this.client = undefined;
            this.history = undefined;
            this.occl = undefined;
            this.fromStorage = false;
            this.showReferences = false;
            this.consentGroups = undefined;
            this.identity = undefined;
            this.error = undefined;
            this.references = [];
            this.showVersions = false;
            this.isExistingRegister = false;
        });
    }
    /** indicating a fatal error condition when starting up <tef-coms4cs> */
    get fatal() {
        var _a, _b, _c, _d;
        return (_c = (_a = this.error) !== null && _a !== void 0 ? _a : (_b = this.occl) === null || _b === void 0 ? void 0 : _b.error) !== null && _c !== void 0 ? _c : (this.isDataLoaded ? undefined : (_d = this.client) === null || _d === void 0 ? void 0 : _d.error);
    }
    async init(config) {
        var _a, _b, _c, _d;
        this.error = undefined; // reset error state -> important for browser tests
        this.config = {
            ...config,
            apiUri: (_a = config.apiUri) === null || _a === void 0 ? void 0 : _a.replace(/\/$/, ''),
            occlUri: (_b = config.occlUri) === null || _b === void 0 ? void 0 : _b.replace(/\/$/, ''),
            channel: config.channel || 'Shop',
            usertype: config.usertype || 'Agent',
            consentwhitelist: (_c = config.consentwhitelist) === null || _c === void 0 ? void 0 : _c.trim(),
            references: config.references,
            showReferences: config.showReferences || false,
            showVersions: config.showVersions || false,
            isExistingRegister: config.isExistingRegister || false,
            registerId: config.registerId,
        };
        const once = autorun(() => {
            if (this.isDataLoaded && this.isOcclReady) {
                this.service.fireEvent({
                    name: 'load/READY',
                    level: 'info',
                    message: 'micro-frontend is ready',
                });
                once();
            }
            if (this.fatal) {
                this.service.fireEvent({
                    name: 'load/ERROR',
                    level: 'error',
                    error: this.fatal,
                });
                this.tracker.comsError(this.fatal.message);
                once();
            }
        });
        if (this.config.references) {
            try {
                this.references = JSON.parse(this.config.references);
                this.references.forEach((ref) => {
                    if (!isReference(ref)) {
                        const refError = new Error();
                        refError.message = 'invalid object structure';
                        throw refError;
                    }
                });
            }
            catch (error) {
                const errorObject = error;
                const formatError = new Error(`attribute "references" invalid format: ${errorObject.message}`);
                this.error = formatError;
                throw formatError;
            }
        }
        // validate consentwhitelist
        let consentWhiteList;
        if (this.config.consentwhitelist) {
            try {
                consentWhiteList = JSON.parse(this.config.consentwhitelist);
            }
            catch (error) {
                const errorObject = error;
                const formatError = new Error(`attribute "consentwhitelist" invalid JSON format: ${errorObject.message}`);
                this.error = formatError;
                throw formatError;
            }
        }
        try {
            assert(!consentWhiteList || Array.isArray(consentWhiteList), 'attribute "consentwhitelist" is not an array');
            assert(!consentWhiteList || consentWhiteList.reduce((acc, s) => acc && isUuid(s), true), 'attribute "consentwhitelist" contains invalid UUIDs');
            assert(!!this.config.apiUri, 'attribute "apiuri" is not set');
            assert(!!this.config.occlUri, 'attribute "occluri" is not set');
            if (!this.config.agentToken) {
                assert(!!this.config.spId, 'attribute "spId" is not set');
                assert(!!this.config.fetch, 'attribute "fetch" is not set');
                assert(!!this.config.lcId || !!this.config.registerId, 'attribute "lcId" or "registerId" is not set');
            }
            else {
                assert(!!this.config.agentToken, 'attribute "agenttoken" is not set');
            }
            assert(!!this.config.lifecycle, 'attribute "lifecycle" is not set');
            assert(!this.config.mode || this.config.mode === 'overview' || this.config.mode === 'buttons', 'attribute "mode" is wrong');
        }
        catch (error) {
            this.error = error;
            throw error;
        }
        Tracker.init(this.config);
        this.client = new ClientStore(this.config);
        this.occl = new OcclStore(this.config.occlUri, this.config.styleContext);
        this.history = new HistoryStore(this.client);
        const identity = await this.client.getIdentity();
        runInAction(() => {
            this.identity = identity;
        });
        const consentsFromSession = this.$getConsentsFromSession();
        this.fromStorage = !!consentsFromSession;
        const { groups, details } = consentsFromSession !== null && consentsFromSession !== void 0 ? consentsFromSession : await this.client.loadConsents();
        // filter consentGroups whith whitelist if consentwhitelist is set
        // and groups are not loaded from session
        let filteredGroups;
        if (consentWhiteList && !this.fromStorage) {
            filteredGroups = groups
                .map((group) => ({
                ...group,
                consents: group.consents.filter((consent) => consentWhiteList === null || consentWhiteList === void 0 ? void 0 : consentWhiteList.includes(consent.id)),
            }))
                .filter((group) => group.consents.length > 0);
        }
        runInAction(() => {
            this.details = details;
            this.consentGroups = (filteredGroups !== null && filteredGroups !== void 0 ? filteredGroups : groups)
                .map((group) => new ConsentGroupStore(group, this.fromStorage));
        });
        if (consentsFromSession) {
            this.service.fireEvent({
                name: 'consents/LOAD',
                level: 'info',
                message: 'loading consents successful',
            });
        }
        if (((_d = this.consentGroups) === null || _d === void 0 ? void 0 : _d.length) === 0) {
            this.service.fireEvent({
                name: 'consents/EMPTY',
                level: 'info',
                message: 'no consents to be rendered',
            });
        }
        autorun(() => {
            if (this.isDataLoaded) {
                const snapshot = this.toJSON();
                SessionStorage.setItem(ComsStore.STORAGE_NAME, snapshot);
            }
        });
    }
    get isDataLoaded() {
        return !!this.consentGroups;
    }
    get isOcclReady() {
        var _a, _b;
        return (_b = (_a = this.occl) === null || _a === void 0 ? void 0 : _a.ready) !== null && _b !== void 0 ? _b : false;
    }
    get hasModifiedConsents() {
        return !!this.consents.find((c) => c.modified) || !!this.consents.find((c) => c.modeconstraint === 'edit');
    }
    /** Flat list of consents. */
    get consents() {
        if (!this.consentGroups)
            return [];
        return this.consentGroups
            .reduce((acc, { consents }) => [...acc, ...consents], []);
    }
    findGroupByName(groupName) {
        var _a;
        return (_a = this.consentGroups) === null || _a === void 0 ? void 0 : _a.find((group) => group.name === groupName);
    }
    async save() {
        var _a, _b;
        const refs = this.references.filter((r) => !!r.id);
        await ((_a = this.client) === null || _a === void 0 ? void 0 : _a.saveConsents(this.consents.map((consent) => consent.toSaveConsent()), refs.length > 0
            ? refs.map(({ state, ...ref }) => ref)
            : undefined));
        this.tracker.changePermissions((_b = this.consentGroups) !== null && _b !== void 0 ? _b : []);
        this.consents.forEach((cons) => cons.setModified(false));
        SessionStorage.setItem(ComsStore.STORAGE_NAME, undefined);
    }
    getShortConsents() {
        return this.consents.map((consent) => consent.toShortConsent());
    }
    getConsentStatus() {
        return this.consents.map((consent) => consent.toSaveConsent());
    }
    toJSON() {
        var _a, _b, _c;
        return {
            page: {
                groups: (_b = (_a = this.consentGroups) === null || _a === void 0 ? void 0 : _a.map((cg) => cg.toJSON())) !== null && _b !== void 0 ? _b : [],
                details: (_c = this.details) !== null && _c !== void 0 ? _c : {},
            },
            identity: this.identity,
            config: this.config,
        };
    }
    $getConsentsFromSession() {
        try {
            const dataInStorage = SessionStorage.getItem(ComsStore.STORAGE_NAME);
            if (!dataInStorage)
                return undefined;
            const match = () => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                return (dataInStorage.config.lifecycle === ((_a = this.config) === null || _a === void 0 ? void 0 : _a.lifecycle)
                    && dataInStorage.config.gatewayToken === ((_b = this.config) === null || _b === void 0 ? void 0 : _b.gatewayToken)
                    && dataInStorage.config.apiUri === ((_c = this.config) === null || _c === void 0 ? void 0 : _c.apiUri)
                    && dataInStorage.identity.username === ((_d = this.identity) === null || _d === void 0 ? void 0 : _d.username)
                    && dataInStorage.identity.lcId === ((_e = this.identity) === null || _e === void 0 ? void 0 : _e.lcId)
                    && dataInStorage.identity.spId === ((_f = this.identity) === null || _f === void 0 ? void 0 : _f.spId)
                    && dataInStorage.identity.registerId === ((_g = this.identity) === null || _g === void 0 ? void 0 : _g.registerId)
                    && dataInStorage.identity.readonly === ((_h = this.identity) === null || _h === void 0 ? void 0 : _h.readonly)
                    && dataInStorage.config.lcId === ((_j = this.config) === null || _j === void 0 ? void 0 : _j.lcId)
                    && dataInStorage.config.registerId === ((_k = this.config) === null || _k === void 0 ? void 0 : _k.registerId)
                    && dataInStorage.config.spId === ((_l = this.config) === null || _l === void 0 ? void 0 : _l.spId));
            };
            return match() ? dataInStorage.page : undefined;
        }
        catch (error) { // ignore error
            return undefined;
        }
    }
}
ComsStore.STORAGE_NAME = 'tef-coms4cs-state';
const comsStore = new ComsStore();
export default comsStore;
//# sourceMappingURL=ComsStore.js.map